import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IBulkVerifyPendingTransaction } from 'src/app/dashboard/interfaces/IBulkVerifyPendingTransaction';
import { ICompoundDetailsModel } from 'src/app/inventory/interfaces/compound-details.interface';
import { ICompoundIndexModel } from 'src/app/inventory/interfaces/compound-index-model.interface';
import { IIngredientWasteForAmend } from 'src/app/inventory/interfaces/ingredient-for-amend.interface';
import { endpoints } from '../../constants/endpoints';
import { objectToQuerystring } from '../../helpers/helpers';
import { ActivityTypesEnum } from '../../interfaces/ActivityTypesEnum';
import { IAmendTransaction } from '../../interfaces/amend-transaction.interface';
import { ICustomDrug } from '../../interfaces/custom-drug-interface';
import { IConfirmedPendingTransactionResult } from '../../interfaces/IConfirmedPendingTransactionResult';
import { IDupTransaction } from '../../interfaces/IDupTransaction';
import { IInventory, IInventoryListResponse } from '../../interfaces/inventory.interface';
import { IResponse } from '../../interfaces/IResponse';
import { ITransactionModel } from '../../interfaces/ITransactionModel';
import { IUnnecessaryToConfirmOwedPendingTransactionDialogState } from '../../interfaces/IUnnecessaryToConfirmOwedPendingTransactionDialogState';
import { IPendingTransactionDetails } from '../../interfaces/pending-transaction-details.interface';
import { IPendingTransaction } from '../../interfaces/pending-transaction.interface';
import { IRecord } from '../../interfaces/record.interface';
import { TransactionSearchCriteria } from '../../interfaces/search-criteria';
import { SearchDateRange } from '../../interfaces/SearchDateRange';
import { ITransaction } from '../../interfaces/transaction.interface';
import { TransactionsService } from '../../services/transactions.service';
import { AuthState } from '../auth/auth.state';
import { AppendCustomDrug, ClearDrugsFilter } from '../drugs/drugs.actions';
import { InventoryStateModel } from '../inventory/inventory.state';
import * as Act from './transactions.actions';
import { IInventoryModel } from '../../interfaces/IInventoryModel';
import { PendingTransactionsService } from 'src/app/dashboard/services/pending-transactions.service';

export interface TransactionsStateModel {
  response: IResponse;
  loading: boolean;
  success: boolean;
  currentRecord: IRecord;
  drugTransactions: ITransaction[];
  widgetData: ITransaction;
  widgetHistory: ITransaction[];
  widgetHistoryHasMore: boolean;
  transactions: ITransaction[];
  currentPageTransactions: ITransactionModel[];
  transactionsForScroll: ITransactionModel[];
  inventoryList: IInventory[];
  currentTransaction: ITransaction;
  currentInventory: IInventory;
  dashboardSummary: any;
  archivingStatus: boolean;
  amendReasonsList: any[];
  createByID?: string;
  amendmentInfo: IAmendTransaction;
  pendingTransactions: IPendingTransaction[];
  bulkVerifyPendingTransactions: IBulkVerifyPendingTransaction;
  currentPendingTransaction: IPendingTransactionDetails;
  unconfirmedDispenseId: number;
  carouselDisplayList: IPendingTransaction[];
  recentConfirmedTransactionOperationResult: IConfirmedPendingTransactionResult;
  transactionDateChangeHistory: {};
  mostRecentAuditTransactionDate: Date;
  duplicateTransactions: IDupTransaction[];
  currentPendingTransactionRemoved: boolean;
  dashboardFilters: any;
  currentPage: number;
  unnecessaryToConfirmOwedPendingTransactionDialogState: IUnnecessaryToConfirmOwedPendingTransactionDialogState;
  bulkVerifyInProgress: boolean;
  bulkVerifyLoadedPercent: number;
  compounds: ICompoundIndexModel[];
  compoundDetails: ICompoundDetailsModel;
  ingredientsAndWasteForTheCurrentAmend: IIngredientWasteForAmend[];
  currentIngredientsFromTheLatestCompounding: IInventoryModel[];
}

@State<TransactionsStateModel>({
  name: 'transactions',
})

@Injectable()
export class TransactionsState {

  constructor(
    private http: HttpClient,
    private store: Store,
    private transactionService: TransactionsService,
    private pendingTransactionsService: PendingTransactionsService
  ) {
  }

  @Selector()
  static response(state: InventoryStateModel): IResponse {
    return state.response;
  }


  @Selector()
  static loading(state: TransactionsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: TransactionsStateModel): boolean {
    return state.success;
  }

  @Selector()
  static currentRecord(state: TransactionsStateModel): IRecord {
    return state.currentRecord;
  }

  @Selector()
  static currentInventory(state: TransactionsStateModel): IInventory {
    return state.currentInventory;
  }

  @Selector()
  static currentTransaction(state: TransactionsStateModel): ITransaction {
    return state.currentTransaction;
  }

  @Selector()
  static drugTransactions(state: TransactionsStateModel): ITransaction[] {
    return state.drugTransactions.map(el => ({...el, user: el.firstName + ' ' + el.lastName}));
  }

  @Selector()
  static transactions(state: TransactionsStateModel): ITransaction[] {
    return state.transactions;
  }

  @Selector()
  static widgetData(state: TransactionsStateModel): ITransaction {
    return state.widgetData;
  }

  @Selector()
  static widgetHistory(state: TransactionsStateModel): ITransaction[] {
    return state.widgetHistory;
  }

  @Selector()
  static widgetHistoryHasMore(state: TransactionsStateModel): boolean {
    return state.widgetHistoryHasMore;
  }

  @Selector()
  static inventoryList(state: IInventoryListResponse): IInventory[] {
    return state.inventory;
  }

  @Selector()
  static inventoryActiveList(state: IInventoryListResponse): IInventory[] {
    return state.inventory.filter(el => el.active);
  }

  @Selector()
  static inventoryArchivedList(state: IInventoryListResponse): IInventory[] {
    return state.inventory.filter(el => !el.active);
  }

  @Selector()
  static dashboardSummary(state: TransactionsStateModel): any {
    return state.dashboardSummary;
  }

  @Selector()
  static archivingStatus(state: TransactionsStateModel): boolean {
    return state.archivingStatus;
  }

  @Selector()
  static amendReasonsList(state: TransactionsStateModel): any[] {
    return state.amendReasonsList;
  }

  @Selector()
  static pendingTransactions(state: TransactionsStateModel): any[] {
    return state.pendingTransactions;
  }

  @Selector()
  static currentPendingTransactionDetails(state: TransactionsStateModel): IPendingTransactionDetails {
    return state.currentPendingTransaction;
  }

  @Selector()
  static unconfirmedDispenseId(state: TransactionsStateModel): number {
    return state.unconfirmedDispenseId;
  }

  @Selector()
  static recentConfirmedTransactionOperationResult(state: TransactionsStateModel): IConfirmedPendingTransactionResult {
    return state.recentConfirmedTransactionOperationResult;
  }

  @Selector()
  static transactionDateChangeHistory(state: TransactionsStateModel): any {
    return state.transactionDateChangeHistory;
  }

  @Selector()
  static mostRecentAuditTransactionDate(state: TransactionsStateModel): any {
    return state.mostRecentAuditTransactionDate;
  }

  @Selector()
  static duplicateTransactions(state: TransactionsStateModel): IDupTransaction[] {
    return state.duplicateTransactions || [];
  }

  @Selector()
  static transactionsForScroll(state: TransactionsStateModel): ITransactionModel[] {
    return state.transactionsForScroll;
  }

  @Selector()
  static dashboardFilters(state: TransactionsStateModel): any {
    return state.dashboardFilters;
  }

  @Selector()
  static currentPage(state: TransactionsStateModel): number {
    return state.currentPage;
  }

  @Selector()
  static unnecessaryToConfirmOwedPendingTransactionDialogState(state: TransactionsStateModel): IUnnecessaryToConfirmOwedPendingTransactionDialogState {
    return state.unnecessaryToConfirmOwedPendingTransactionDialogState;
  }

  @Selector()
  static bulkVerifyInProgress(state: TransactionsStateModel): boolean {
    return state.bulkVerifyInProgress;
  }

  @Selector()
  static bulkVerifyLoadedPercent(state: TransactionsStateModel): number {
    return state.bulkVerifyLoadedPercent;
  }

  @Selector()
  static compounds(state: TransactionsStateModel): ICompoundIndexModel[] {
    return state.compounds;
  }

  @Selector()
  static compoundDetails(state: TransactionsStateModel): ICompoundDetailsModel {
    return state.compoundDetails;
  }

  @Selector()
  static ingredientsAndWasteForTheCurrentAmend(state: TransactionsStateModel): IIngredientWasteForAmend[] {
    return state.ingredientsAndWasteForTheCurrentAmend;
  }

  @Selector()
  static currentIngredientsFromTheLatestCompounding(state: TransactionsStateModel): IInventoryModel[] {
    return state.currentIngredientsFromTheLatestCompounding;
  }


  @Action(Act.GetDashboardSummary)
  getDashboardSummary(ctx: StateContext<TransactionsStateModel>, {}: Act.GetDashboardSummary): Observable<any> {
    ctx.patchState({loading: true, dashboardSummary: {}});

    return this.http.post(endpoints.transaction.getDashboardSummary, {})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            dashboardSummary: resp.value,
            // TODO: fetch props to the top of dashboard page
            // value: {
            //   currentTransactionSummary: {
            //     allTransactionsCount: 0,
            //     myTransactionsCount: 0,
            //     amendmentsCount: 0
            //   },
            //   previousTransactionSummary: {
            //     allTransactionsCount: 0,
            //     myTransactionsCount: 0,
            //     amendmentsCount: 0
            //   },
            //   allTransactionsCountPercentageOfVariation: 0,
            //   myTransactionsCountPercentageOfVariation: 0,
            //   amendmentsCountPercentageOfVariation: 0
            // },
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetWidgetData)
  setWidgetRecord(ctx: StateContext<TransactionsStateModel>, {data}: Act.SetWidgetData): void {
    ctx.patchState({widgetData: data});
  }

  @Action(Act.SetDrugTransactions)
  setDrugTransactions(ctx: StateContext<TransactionsStateModel>, {transactions}: Act.SetDrugTransactions): void {
    ctx.patchState({transactions: transactions || []});
  }

  @Action(Act.SetTransactionsList)
  setTransactionsList(ctx: StateContext<TransactionsStateModel>, {transactions}: Act.SetTransactionsList): void {
    ctx.patchState({transactions: transactions || []});
  }

  @Action(Act.SetCarouselDisplayList)
  setCarouselDisplayList(ctx: StateContext<TransactionsStateModel>, {pendingTransactions}: Act.SetCarouselDisplayList): void {
    ctx.patchState({carouselDisplayList: pendingTransactions || []});
  }

  @Action(Act.SetTransactionsForScroll)
  setTransactionsForScroll(ctx: StateContext<TransactionsStateModel>, {transactions}: Act.SetTransactionsForScroll): void {
    ctx.patchState({transactionsForScroll: transactions || []});
  }

  @Action(Act.SetDashboardFilters)
  setDashboardFilters(ctx: StateContext<TransactionsStateModel>, {filters}: Act.SetDashboardFilters): void {
    ctx.patchState({dashboardFilters: filters});
  }

  @Action(Act.SetCurrentPage)
  setCurrentPage(ctx: StateContext<TransactionsStateModel>, {page}: Act.SetCurrentPage): void {
    ctx.patchState({currentPage: page});
  }

  @Action(Act.SetBulkVerifyLoadedPercent)
  setBulkVerifyLoadedPercent(ctx: StateContext<TransactionsStateModel>, {percent}: Act.SetBulkVerifyLoadedPercent): void {
    ctx.patchState({bulkVerifyLoadedPercent: percent});
    if (percent == 100) {
      setTimeout(() => {
        ctx.patchState({bulkVerifyInProgress: false});
      }, 1000);
    }
  }

  @Action(Act.ResetPotentialsDuplicates)
  resetPotentialsDuplicates(ctx: StateContext<TransactionsStateModel>, {}: Act.ResetPotentialsDuplicates): void {
    ctx.patchState({duplicateTransactions: []});
  }

  @Action(Act.ClearPendingTransactions)
  clearPendingTransactions(ctx: StateContext<TransactionsStateModel>, {}: Act.ClearPendingTransactions): void {
    ctx.patchState({pendingTransactions: []});
  }

  @Action(Act.GetInventoryTransactions)
  getInventoryTransactions(ctx: StateContext<TransactionsStateModel>, {drugID}: Act.GetInventoryTransactions): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true, drugTransactions: []});
    return this.http.get(endpoints.inventory.getInventoryByDrugID + pharmacyID + '/' + drugID) // + `?params=${filterData}`
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              drugTransactions: resp.value?.transactions || [],
              loading: false,
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }


  @Action(Act.GetWidgetHistory)
  getWidgetHistory(ctx: StateContext<TransactionsStateModel>, {drugId}: Act.GetWidgetHistory): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    ctx.patchState({loading: true, widgetHistory: []});

    const historyCriteria = new TransactionSearchCriteria();
    historyCriteria.drugId = drugId;
    historyCriteria.pharmacyId = pharmacyId;
    historyCriteria.page = 0;
    // ask for six so we know if we need to show View More
    historyCriteria.pageSize = 6;
    historyCriteria.searchDateRange = SearchDateRange.All;
    historyCriteria.withRecentTransactionDateChangeHistory = true;
    delete historyCriteria.schedules;
    const params = objectToQuerystring('criteria', historyCriteria);
    return this.http.get(endpoints.transaction.searchWithDetails + '?' + params)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            // only take the top 5
            widgetHistory: (resp?.value?.transactions || []).slice(0, 5),
            widgetHistoryHasMore: (resp?.value?.transactions || []).length > 5,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAllTransactionsWithDetails)
  getAllTransactions(ctx: StateContext<TransactionsStateModel>, {criteria}: Act.GetAllTransactionsWithDetails): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true, transactions: []});

    if (!criteria) {
      criteria = new TransactionSearchCriteria();
    }

    criteria.pharmacyId = pharmacyId;
    delete criteria.schedules;
    const params = objectToQuerystring('criteria', criteria);

    return this.http.get(endpoints.transaction.searchWithDetails + '?' + params)
      .pipe(
        tap((resp: any) => {
          if (criteria?.page === 0) {
            ctx.patchState({transactions: []});
          }
          ctx.patchState({
            transactions: this.transactionService.mapTransactions(resp.value?.transactions || []),
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAllTransactionsWithoutDetails)
  getAllTransactionsWithoutDetails(ctx: StateContext<TransactionsStateModel>, {criteria}: Act.GetAllTransactionsWithoutDetails): Observable<any> {
   
    ctx.patchState({loading: true});

    if (!criteria) {
      criteria = new TransactionSearchCriteria();
    }
    
    const params = objectToQuerystring('criteria', criteria);
    return this.http.post(endpoints.transaction.searchWithoutDetails, criteria)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            currentPageTransactions: this.transactionService.mapTransactions(resp.value || []),
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAmendReasonsList)
  getAmendReasonsList(ctx: StateContext<TransactionsStateModel>, {}: Act.GetAmendReasonsList): Observable<any> {
    return this.http.get(endpoints.transaction.getAmendReasons)
      .pipe(
        tap((resp: any) => {
          if (resp) {
            const amendList = resp.map(el => ({id: el.key, name: el.value}));
            ctx.patchState({
              amendReasonsList: amendList
            });
          } else {
            ctx.patchState({
              amendReasonsList: []
            });
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.SetTransaction)
  setTransaction(ctx: StateContext<TransactionsStateModel>, {transaction}: Act.SetTransaction): void {
    ctx.patchState({currentTransaction: transaction});
  }

  private setDrugStatus(ctx: StateContext<TransactionsStateModel>, pharmacyID: string, drugID: string, active: boolean)
    : Observable<boolean> {
    ctx.patchState({loading: true});

    return this.http.post(`${endpoints.inventory.setStatus}/${pharmacyID}/${drugID}?active=${active}`, null)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              archivingStatus: true,
              loading: false,
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, archivingStatus: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetTransaction)
  getTransaction(ctx: StateContext<TransactionsStateModel>, {id, shouldOpenWidget}: Act.GetTransaction): Observable<any> {
    ctx.patchState({loading: true, currentTransaction: {} as ITransaction});

    return this.http.get(endpoints.transaction.getTransaction + id)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            if (shouldOpenWidget) {
              ctx.patchState({
                widgetData: resp?.value?.transaction,
              });
            }
            ctx.patchState({
              currentTransaction: resp.value,
              loading: false,
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreateTransaction)
  createTransaction(ctx: StateContext<TransactionsStateModel>, {transactions}: Act.CreateTransaction): Observable<any> {
    ctx.patchState({loading: true, success: null});

    transactions.forEach(el => {
      el.pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
      el.createByID = this.store.selectSnapshot(AuthState.activeUser).id;
    });

    return this.http.put(endpoints.transaction.createTransaction, transactions)
      .pipe(
        tap((responseData: any) => {
          if(responseData?.success && ctx.getState().pendingTransactions) {
          let pendingTransactionsWithNewInventory: IPendingTransaction[] = [];
          ctx.getState().pendingTransactions.map(pTr => {
           transactions.some(tr => pendingTransactionsWithNewInventory.push(tr.drugId == pTr.drugId.toString() ?
           {...pTr, inventoryOnHand: pTr.inventoryOnHand + +tr.addedQuantity} : pTr ))
          });

          ctx.patchState({
            pendingTransactions: pendingTransactionsWithNewInventory
          })
            this.store.dispatch(new ClearDrugsFilter());
          }
          ctx.patchState({
            loading: false,
            success: responseData.success,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AmendTransaction)
  amendTransaction(ctx: StateContext<TransactionsStateModel>, {amendData}: Act.AmendTransaction): Observable<any> {
    ctx.patchState({loading: true, success: null});
    amendData.createById = this.store.selectSnapshot(AuthState.activeUser).id;
    return this.http.put(endpoints.transaction.createAmendment, amendData)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            success: resp.success
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AmendCompound)
  amendCompound(ctx: StateContext<TransactionsStateModel>, {request}: Act.AmendCompound): Observable<any> {
    ctx.patchState({loading: true, success: null});
    return this.http.post(endpoints.transaction.amendCompound, request)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            success: resp.success
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  
  @Action(Act.UpdateTransactionDate)
  updateTransactionDate(ctx: StateContext<TransactionsStateModel>, {payload}: Act.UpdateTransactionDate): Observable<any> {
    ctx.patchState({loading: true, response: null, transactionDateChangeHistory: {}});

    return this.http.post(endpoints.transaction.updateTransactionDate, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPendingTransactions)
  getPendingTransactions(ctx: StateContext<TransactionsStateModel>, {}: Act.GetPendingTransactions): Observable<any> {
    ctx.patchState({loading: true});

    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    if (!pharmacyId || pharmacyId === 'undefined') {
      return;
    }
    return this.http.get(endpoints.integration.getAllPendingTransactions + "/?id=" + pharmacyId)
      .pipe(
        tap((resp: any) => {
          const pendingTransactions = resp?.value;
          this.pendingTransactionsService.updateWithAbbreviatedDescriptions(pendingTransactions);
          ctx.patchState({
            loading: false,
            pendingTransactions,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPendingTransactionsForBulkVerify)
  getPendingTransactionsForBulkVerify(ctx: StateContext<TransactionsStateModel>, {locationID}: Act.GetPendingTransactionsForBulkVerify): Observable<any> {
    ctx.patchState({loading: true, bulkVerifyPendingTransactions: null});

    let url = endpoints.integration.getBulkVerifyPendingTransactions;

    if (locationID) {
      url += '/' + locationID;
    }
    return this.http.get(url)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            bulkVerifyPendingTransactions: resp?.value,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPendingTransactionDetails)
  getPendingTransactionDetails(ctx: StateContext<TransactionsStateModel>, {id}: Act.GetPendingTransactionDetails): Observable<any> {
    ctx.patchState({loading: true, currentPendingTransaction: null, unconfirmedDispenseId: null, unnecessaryToConfirmOwedPendingTransactionDialogState: null});

    return this.http.get(endpoints.integration.getPendingTransaction + "/?id=" + id)
      .pipe(
        tap((resp: any) => {

          ctx.patchState({
            loading: false
          });

          if (resp?.success) {
            if (!resp?.value) {
              ctx.patchState({
                pendingTransactions: ctx.getState().pendingTransactions.filter(x => x.id !== id)
              });
            }
            else if (resp?.value?.unnecessaryToConfirmOwedPendingTransactionState) {
              ctx.patchState({
                unnecessaryToConfirmOwedPendingTransactionDialogState: {...resp.value.unnecessaryToConfirmOwedPendingTransactionState, idToRemove: id}
              });
            }
            else {
              ctx.patchState({
                currentPendingTransaction: resp?.value
              });
            }
          } else {
            if (resp?.value)
              ctx.patchState({ unconfirmedDispenseId: resp.value });
            else {
              console.warn(resp?.message);
              throw new Error(resp?.message);
            }
          }

        }),
        catchError((err) => {
          ctx.patchState({ loading: false });
          return of(null);
        }),
      );
  }

  @Action(Act.RefreshPendingTransactionDetails)
  refreshPendingTransactionDetails(ctx: StateContext<TransactionsStateModel>, {id}: Act.RefreshPendingTransactionDetails): Observable<any> {
    ctx.patchState({loading: true, currentPendingTransactionRemoved: false});

    return this.http.get(endpoints.integration.refreshPendingTransactionDetails + "/?id=" + id)
      .pipe(
        tap((resp: any) => {
          if (!resp?.value) {
            ctx.patchState({ loading: false, currentPendingTransactionRemoved: true });
          }
          else if (resp?.success && resp.value) {
            const all = ctx.getState().pendingTransactions;
            ctx.patchState({
              loading: false,
              currentPendingTransaction: resp?.value,
              pendingTransactions: all.map(x => (x.id === resp?.value?.id ? {...x, friendlyName: resp?.value?.friendlyName, patientName: resp?.value?.patientName, description: resp?.value?.description} : x ))
            });
          } else {
            if (resp?.value)
              ctx.patchState({ unconfirmedDispenseId: resp.value });
            else {
              console.warn(resp?.message);
              throw new Error(resp?.message);
            }
          }

        }),
        catchError((err) => {
          ctx.patchState({ loading: false });
          return of(null);
        }),
      );
  }

  @Action(Act.ConfirmTransaction)
  confirmTransaction(ctx: StateContext<TransactionsStateModel>, {payload}: Act.ConfirmTransaction): Observable<any> {
    ctx.patchState({loading: true, unconfirmedDispenseId: null, recentConfirmedTransactionOperationResult: null});

    payload.pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    return this.http.patch(endpoints.integration.confirmTansaction, payload)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            ctx.patchState({
              pendingTransactions: payload.isC2KeepPartialDispense ? 
                ctx.getState().pendingTransactions.map(x => (x.id === payload.id) ? {...x, 
                  addedQuantity: resp?.value?.addedQuantity, 
                  isC2KeepPartialDispense: true,
                  description: resp?.value?.description || x.description,
                  inventoryOnHand: 0
                } : x) :
                ctx.getState().pendingTransactions.filter(x => x.id !== payload.id),
              recentConfirmedTransactionOperationResult: resp?.value
            });
          } else {
            if (resp?.value)
              ctx.patchState({ unconfirmedDispenseId: resp.value });
          }
          ctx.patchState({
            loading: false
          });

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ConfirmSplitNdcsDispense)
  confirmSplitNdcsDispenseTransaction(ctx: StateContext<TransactionsStateModel>, { payload }: Act.ConfirmSplitNdcsDispense): Observable<any> {
    ctx.patchState({ loading: true });

    payload.pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    return this.http.patch(endpoints.integration.confirmSplitNdcsDispense, payload)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            ctx.patchState({
              loading: false,
              pendingTransactions: ctx.getState().pendingTransactions.filter(x => x.id !== payload.id)
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({ loading: false });
          return of(null);
        }),
      );
  }

  @Action(Act.ConfirmSplitNdcsAmendment)
  confirmSplitNdcsAmendment(ctx: StateContext<TransactionsStateModel>, { payload }: Act.ConfirmSplitNdcsAmendment): Observable<any> {
    ctx.patchState({ loading: true });

    payload.pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    return this.http.patch(endpoints.integration.confirmSplitNdcsAmendment, payload)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            ctx.patchState({
              loading: false,
              pendingTransactions: ctx.getState().pendingTransactions.filter(x => x.id !== payload.id)
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({ loading: false });
          return of(null);
        }),
      );
  }

  @Action(Act.InvalidateTransaction)
  invalidateTransaction(ctx: StateContext<TransactionsStateModel>, {payload}: Act.InvalidateTransaction): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.patch(endpoints.integration.invalidateTransaction, payload)
      .pipe(
        tap((resp: any) => {
          if (resp?.success && resp?.value) {
            ctx.patchState({
              pendingTransactions: ctx.getState().pendingTransactions.filter(x => !(<number[]>resp.value).includes(x.id)),
              loading: false
            });
          }

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AddPendingTransaction)
  addPendingTransaction(ctx: StateContext<TransactionsStateModel>, { pendingTransaction }: Act.AddPendingTransaction): void {
    if (!ctx.getState().pendingTransactions?.find(x => x.id === pendingTransaction?.id))  {
      this.pendingTransactionsService.updateSingleWithAbbreviatedDescription(pendingTransaction);
      ctx.patchState({ pendingTransactions: [...ctx.getState().pendingTransactions, pendingTransaction] });
    }
  }

  @Action(Act.UpdatePendingTransaction)
  updatePendingTransaction(ctx: StateContext<TransactionsStateModel>, { pendingTransaction }: Act.UpdatePendingTransaction): void {
    this.pendingTransactionsService.updateSingleWithAbbreviatedDescription(pendingTransaction);
    ctx.patchState({
      pendingTransactions: ctx.getState().pendingTransactions?.map(x => (x.id === pendingTransaction?.id ?
        {
          ...x,
          friendlyName: pendingTransaction?.friendlyName ?? x.friendlyName,
          patientName: pendingTransaction?.patientName ?? x.patientName,
          description: pendingTransaction?.description,
          inventoryOnHand: pendingTransaction?.inventoryOnHand ?? x.inventoryOnHand,
          isPartialFill: pendingTransaction?.isPartialFill ?? x.isPartialFill,
          partialNumber: pendingTransaction?.partialNumber ?? x.partialNumber,
        } : x))
    });
  }

  @Action(Act.DeletePendingTransactionFromView)
  deletePendingTransactionFromView(ctx: StateContext<TransactionsStateModel>, { id }: Act.DeletePendingTransactionFromView): void {
    ctx.patchState({ pendingTransactions: ctx.getState()?.pendingTransactions.filter(x => x.id !== id) });
  }

  @Action(Act.GetTransactionDateHistory)
  getTransactionDateHistory(ctx: StateContext<TransactionsStateModel>, {transactionId}: Act.GetTransactionDateHistory): Observable<any> {
    ctx.patchState({loading: true, transactionDateChangeHistory: {}});
    return this.http.get(endpoints.transaction.getTransactionDateChangeHistory + transactionId)
    .pipe(
      tap((resp: any) => {
        if (resp?.success && resp?.value) {
          ctx.patchState({
            transactionDateChangeHistory: {transactionId, history: resp.value},
            loading: false
          });
        }

      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.GetMostRecentAuditTransactionDate)
  getMostRecentAuditTransactionDate(ctx: StateContext<TransactionsStateModel>, {drugId, locationId}: Act.GetMostRecentAuditTransactionDate): Observable<any> {
    ctx.patchState({loading: true, mostRecentAuditTransactionDate: null});
    return this.http.get(endpoints.transaction.getMostRecentAuditTransactionDate + drugId + '/' + locationId)
    .pipe(
      tap((resp: any) => {
        if (resp?.success && resp?.value) {
          ctx.patchState({
            mostRecentAuditTransactionDate: resp.value,
            loading: false
          });
        }

      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.ValidateTransactionDateForPendingTransactionAmend)
  validateTransactionDateForPendingTransactionAmend(ctx: StateContext<InventoryStateModel>, {payload}:
  Act.ValidateTransactionDateForPendingTransactionAmend): Observable<IResponse> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.integration.validateTransactionDateForPendingTransactionAmend, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.TestForDupTransactions)
  testForDupTransactions(ctx: StateContext<TransactionsStateModel>, {payload}:
  Act.TestForDupTransactions): Observable<any> {
    ctx.patchState({loading: true, duplicateTransactions: null});
    return this.http.post(endpoints.transaction.testForDupTransactions, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            duplicateTransactions: resp?.value?.duplicates
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.MarkPendingTransactionAsConfirmed)
  markPendingTransactionAsConfirmed(ctx: StateContext<TransactionsStateModel>, {payload}:
  Act.MarkPendingTransactionAsConfirmed): Observable<any> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.integration.markPendingTransactionAsConfirmed, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.GetUnconfirmedPendingDispenseOwedQuantity)
  getUnconfirmedPendingDispenseOwedQuantity(ctx: StateContext<TransactionsStateModel>, {rxNumber, refillNumber}:
  Act.GetUnconfirmedPendingDispenseOwedQuantity): Observable<any> {
    ctx.patchState({loading: true, response: null});
    return this.http.get(endpoints.integration.getUnconfirmedPendingDispenseOwedQuantity + rxNumber + '/' + refillNumber)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp?.value
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.CloseNoNeedToConfirmOwedDialogAndRemoveCard)
  closeNoNeedToConfirmOwedDialogAndRemoveCard(ctx: StateContext<TransactionsStateModel>, {}: Act.CloseNoNeedToConfirmOwedDialogAndRemoveCard): void {
  const dialogState = ctx.getState().unnecessaryToConfirmOwedPendingTransactionDialogState;
  ctx.patchState({ pendingTransactions: ctx.getState()?.pendingTransactions.filter(x => x.id !== dialogState.idToRemove), unnecessaryToConfirmOwedPendingTransactionDialogState: null });
  }
  
  @Action(Act.BulkVerifyValidateNdcTransactionDate)
  bulkVerifyValidateNdcTransactionDate(ctx: StateContext<TransactionsStateModel>, {payload}:
  Act.BulkVerifyValidateNdcTransactionDate): Observable<any> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.integration.bulkVerifyValidateTrDate, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp?.value
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.BulkVerify)
  bulkVerify(ctx: StateContext<TransactionsStateModel>, {payload}:
  Act.BulkVerify): Observable<any> {
    ctx.patchState({loading: true, response: null});
    return this.http.patch(endpoints.integration.bulkVerify, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp,
          });
          if (resp.success) {
            ctx.patchState({
              bulkVerifyInProgress: true,
              
            })
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.IsBulkVerifyInProgress)
  isBulkVerifyInProgress(ctx: StateContext<TransactionsStateModel>, {}:
  Act.IsBulkVerifyInProgress): Observable<any> {
    ctx.patchState({response: null});
    return this.http.get(endpoints.integration.isBulkVerifyInProgress)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            bulkVerifyInProgress: resp?.value?.isInProgress,
            bulkVerifyLoadedPercent: resp?.value?.loadedPercent || 0,
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.GetCompounds)
  getCompounds(ctx: StateContext<TransactionsStateModel>, {}:
  Act.GetCompounds): Observable<any> {
    ctx.patchState({response: null});
    return this.http.get(endpoints.transaction.getCompounds)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
           compounds: resp?.value
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.CreateCompound)
  createCompound(ctx: StateContext<TransactionsStateModel>, {request}: Act.CreateCompound): Observable<TransactionsStateModel> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.drug.createCompound, request)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
            const added = responseData?.value as ICompoundIndexModel;
            //unshift created transaction 
            const arr = [...ctx.getState().compounds];
            arr.unshift(added);
            ctx.patchState({
              compounds: arr
            });
            //append a new custom ndc
            this.store.dispatch(new AppendCustomDrug(<unknown>added as ICustomDrug));
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ReceiveCompound)
  receiveCompound(ctx: StateContext<TransactionsStateModel>, {request}: Act.ReceiveCompound): Observable<TransactionsStateModel> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.transaction.receiveCompound, request)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
            const added = responseData?.value as ICompoundIndexModel;
            //unshift created transaction 
            const arr = [...ctx.getState().compounds];
            arr.unshift({...added, activityType: ActivityTypesEnum.Compounded});
            ctx.patchState({
              compounds: arr
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetCompoundDetails)
  getCompoundDetails(ctx: StateContext<TransactionsStateModel>, {id}: Act.GetCompoundDetails): Observable<TransactionsStateModel> {
    ctx.patchState({loading: true, compoundDetails: null});
    return this.http.get(endpoints.transaction.getCompound + '/' + id)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
           ctx.patchState({
            compoundDetails: responseData?.value,
          });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAmendIngredientsWaste)
  getAmendIngredientsWaste(ctx: StateContext<TransactionsStateModel>, {id}: Act.GetAmendIngredientsWaste): Observable<TransactionsStateModel> {
    ctx.patchState({loading: true, ingredientsAndWasteForTheCurrentAmend: []});
    return this.http.get(endpoints.transaction.getAmendIngredientsWaste + '/' + id)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
           ctx.patchState({
            ingredientsAndWasteForTheCurrentAmend: responseData?.value,
          });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetIngredientsFromTheLatestCompounding)
  getIngredientsFromTheLatestCompounding(ctx: StateContext<TransactionsStateModel>, {drugId}: Act.GetIngredientsFromTheLatestCompounding): Observable<TransactionsStateModel> {
    ctx.patchState({loading: true, ingredientsAndWasteForTheCurrentAmend: []});
    return this.http.get(endpoints.transaction.ingredientsFromTheLatestCompounding + '/' + drugId)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
           ctx.patchState({
            currentIngredientsFromTheLatestCompounding: responseData?.value,
          });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ConfirmTheCombinedOwedAndSplitDispense)
  confirmTheCombinedOwedAndSplitDispense(ctx: StateContext<TransactionsStateModel>, { payload }: Act.ConfirmTheCombinedOwedAndSplitDispense): Observable<any> {
    ctx.patchState({ loading: true });

    return this.http.patch(endpoints.integration.confirmTheCombinedOwedAndSplitDispense, payload)
    .pipe(
      tap((resp: any) => {
        if (resp?.success) {
          ctx.patchState({
            pendingTransactions:
              ctx.getState().pendingTransactions.map(x => (x.id === payload.id) ? {...x, 
                addedQuantity: resp?.value?.addedQuantity, 
                isC2KeepPartialDispense: true,
                description: resp?.value?.description || x.description,
                inventoryOnHand: 0
              } : x)
          });
        } else {
          if (resp?.value)
            ctx.patchState({ unconfirmedDispenseId: resp.value });
        }
        ctx.patchState({
          loading: false
        });

      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

}
